import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PantallaCargando } from '@/components/Cargando';
import { BarChart3 } from 'lucide-react';

export default function EmailInformes() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/tenant/email/mass/stats').catch(() => ({ data: {} })),
      api.get('/tenant/email/logs?limit=50').catch(() => ({ data: [] })),
    ]).then(([s, l]) => {
      setStats(s.data || {});
      setLogs(Array.isArray(l.data) ? l.data : (l.data?.items || []));
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <PantallaCargando />;

  const sent = stats.enviados_30d ?? 0;
  const fail = stats.fallidos_30d ?? 0;
  const byDay = stats.por_dia || [];

  return (
    <div className="space-y-5" data-testid="email-informes">
      <div>
        <h2 className="text-base font-semibold text-[#0F172A] flex items-center gap-2">
          <BarChart3 className="w-4 h-4" /> Informes de email
        </h2>
        <p className="text-xs text-[#64748B]">Histórico de envíos y tasa de éxito (30 días).</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border bg-white p-4 text-center">
          <p className="text-3xl font-bold text-emerald-600">{sent}</p>
          <p className="text-[11px] text-[#94A3B8]">Enviados OK</p>
        </div>
        <div className="rounded-xl border bg-white p-4 text-center">
          <p className="text-3xl font-bold text-red-500">{fail}</p>
          <p className="text-[11px] text-[#94A3B8]">Fallidos</p>
        </div>
        <div className="rounded-xl border bg-white p-4 text-center">
          <p className="text-3xl font-bold text-sky-600">
            {sent + fail ? `${Math.round((sent / (sent + fail)) * 100)}%` : '—'}
          </p>
          <p className="text-[11px] text-[#94A3B8]">Tasa de éxito</p>
        </div>
      </div>

      {byDay.length > 0 && (
        <div className="bg-white border rounded-xl p-4">
          <h3 className="text-xs font-semibold text-[#0F172A] mb-3">Envíos por día</h3>
          <div className="flex items-end gap-1 h-28">
            {byDay.map((d) => {
              const max = Math.max(...byDay.map(x => x.total || 0), 1);
              const h = Math.max(4, Math.round(((d.ok || 0) / max) * 100));
              return (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day}: ${d.ok} ok`}>
                  <div className="w-full bg-emerald-400 rounded-t" style={{ height: `${h}%` }} />
                  <span className="text-[8px] text-[#94A3B8] rotate-0">{d.day.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-3 py-2 border-b text-xs font-semibold text-[#0F172A]">Últimos envíos</div>
        <div className="divide-y max-h-80 overflow-y-auto">
          {logs.length === 0 && (
            <p className="p-4 text-xs text-[#94A3B8]">Aún no hay envíos registrados.</p>
          )}
          {logs.map((l, i) => (
            <div key={l.id || i} className="px-3 py-2 flex justify-between gap-2 text-xs">
              <div className="min-w-0">
                <p className="font-medium truncate text-[#0F172A]">{l.subject_preview || l.template_key || '—'}</p>
                <p className="text-[#94A3B8]">{l.created_at ? new Date(l.created_at).toLocaleString('es-MX') : ''}</p>
              </div>
              <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full h-fit ${
                l.status === 'sent' || l.status === 'success' || l.ok
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-red-50 text-red-600'
              }`}>
                {l.status || '—'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
